/**
 * External secret scanner integration
 *
 * Provides optional integration with external tools like gitleaks and trufflehog.
 * Falls back to built-in scanning if external tools are not available.
 */

// Security: Use execFile instead of exec to prevent command injection
// execFile doesn't use shell interpolation, so malicious filenames can't execute arbitrary commands
import { execFile } from 'child_process';
import { promisify } from 'util';
import { readFile, unlink } from 'fs/promises';
import { tmpdir } from 'os';
import { join } from 'path';
import { randomBytes } from 'crypto';
import { z } from 'zod';
import type { SecretMatch, FileScanResult, ScanSummary } from './scanner.js';
import { scanFiles as builtinScanFiles, redactSecret } from './scanner.js';
import type { SecretSeverity } from './patterns.js';
import { collapsePath } from '../paths.js';

const execFileAsync = promisify(execFile);

// ============================================================================
// Types
// ============================================================================

export type ExternalScanner = 'gitleaks' | 'trufflehog' | 'builtin';

// Security: Zod schema for validating gitleaks JSON output
// This prevents prototype pollution and ensures type safety from external tool output
const gitleaksResultSchema = z.object({
  Description: z.string(),
  StartLine: z.number(),
  EndLine: z.number(),
  StartColumn: z.number(),
  EndColumn: z.number(),
  Match: z.string(),
  Secret: z.string(),
  File: z.string(),
  SymlinkFile: z.string().optional().default(''),
  Commit: z.string().optional().default(''),
  Entropy: z.number().optional().default(0),
  Author: z.string().optional().default(''),
  Email: z.string().optional().default(''),
  Date: z.string().optional().default(''),
  Message: z.string().optional().default(''),
  Tags: z.array(z.string()).optional().default([]),
  RuleID: z.string(),
  Fingerprint: z.string().optional().default(''),
});

const gitleaksOutputSchema = z.array(gitleaksResultSchema);

type GitleaksResult = z.infer<typeof gitleaksResultSchema>;

// ============================================================================
// Scanner Detection
// ============================================================================

/**
 * Check if gitleaks is installed
 */
export const isGitleaksInstalled = async (): Promise<boolean> => {
  try {
    // Security: Use execFileAsync to prevent command injection
    await execFileAsync('gitleaks', ['version']);
    return true;
  } catch {
    return false;
  }
};

/**
 * Check if trufflehog is installed
 */
export const isTrufflehogInstalled = async (): Promise<boolean> => {
  try {
    // Security: Use execFileAsync to prevent command injection
    await execFileAsync('trufflehog', ['--version']);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get available scanners
 */
export const getAvailableScanners = async (): Promise<ExternalScanner[]> => {
  const available: ExternalScanner[] = ['builtin'];

  if (await isGitleaksInstalled()) {
    available.push('gitleaks');
  }

  if (await isTrufflehogInstalled()) {
    available.push('trufflehog');
  }

  return available;
};

// ============================================================================
// Gitleaks Integration
// ============================================================================

/**
 * Map gitleaks severity/rule to our severity levels
 */
const mapGitleaksSeverity = (ruleId: string): SecretSeverity => {
  // Critical patterns
  const criticalPatterns = [
    'aws', 'gcp', 'azure', 'private-key', 'stripe', 'github',
    'gitlab', 'npm', 'pypi', 'jwt', 'oauth',
  ];

  // High severity patterns
  const highPatterns = [
    'api', 'token', 'secret', 'password', 'credential',
  ];

  const ruleIdLower = ruleId.toLowerCase();

  if (criticalPatterns.some(p => ruleIdLower.includes(p))) {
    return 'critical';
  }

  if (highPatterns.some(p => ruleIdLower.includes(p))) {
    return 'high';
  }

  return 'medium';
};

/**
 * Generate a placeholder name from gitleaks rule
 */
const generatePlaceholderFromRule = (ruleId: string): string => {
  return ruleId
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
};

/**
 * Convert a validated gitleaks finding into a SecretMatch.
 *
 * SECURITY: `context` is a DISPLAYED field (printed by displayScanResults), so
 * it must never contain the raw secret. gitleaks' `Match` is the matched line
 * including the live secret, so we redact it. `value` still holds the real
 * secret because the redaction pipeline needs it to build placeholder mappings,
 * but it is never printed directly.
 */
/**
 * Approximate the value's content offsets from a gitleaks finding.
 *
 * gitleaks reports only 1-based line/column (`StartColumn`) — NOT a whole-content
 * offset — and `scanWithGitleaks` reads gitleaks' JSON report, not the file
 * content, at this point. We therefore cannot compute exact offsets here: the
 * column-derived `start` only coincides with the true content offset when the
 * secret is on line 1. The returned match is flagged `offsetsExact: false` so
 * that overlap/offset-based consumers ignore these offsets (see `SecretMatch`).
 */
const approximateGitleaksOffsets = (
  finding: GitleaksResult,
  valueLength: number
): { start: number; end: number } => {
  const start = Math.max(0, finding.StartColumn - 1);
  return { start, end: start + valueLength };
};

export const gitleaksResultToMatch = (finding: GitleaksResult): SecretMatch => {
  const secretValue = finding.Secret || finding.Match;
  const { start, end } = approximateGitleaksOffsets(finding, secretValue.length);
  return {
    patternId: `gitleaks-${finding.RuleID}`,
    patternName: finding.Description || finding.RuleID,
    severity: mapGitleaksSeverity(finding.RuleID),
    line: finding.StartLine,
    column: finding.StartColumn,
    value: secretValue,
    redactedValue: redactSecret(secretValue),
    context: redactSecret(finding.Match),
    placeholder: generatePlaceholderFromRule(finding.RuleID),
    start,
    end,
    // Approximate (line/column-derived), NOT exact content offsets. Downstream
    // overlap/offset logic MUST skip matches without offsetsExact === true.
    offsetsExact: false,
  };
};

/**
 * Sentinel value to indicate gitleaks failed for a specific file.
 * We use a Symbol (rather than null or a special error) to distinguish between:
 * - null: gitleaks succeeded but found no secrets
 * - GITLEAKS_FAILED: gitleaks failed (parse error, execution error, etc.)
 * Files marked with this sentinel will be re-scanned using the built-in scanner as a fallback.
 */
const GITLEAKS_FAILED = Symbol('gitleaks-failed');

/**
 * Scan files using gitleaks with fallback to built-in scanner for failed files
 */
export const scanWithGitleaks = async (filepaths: string[]): Promise<ScanSummary> => {
  const results: FileScanResult[] = [];
  let totalSecrets = 0;
  let filesWithSecrets = 0;
  const bySeverity = { critical: 0, high: 0, medium: 0, low: 0 };
  const failedFiles: string[] = []; // Track files where gitleaks failed

  // Run gitleaks on files in parallel batches (with concurrency limit)
  const CONCURRENCY = 5; // Lower concurrency for external process spawning

  for (let i = 0; i < filepaths.length; i += CONCURRENCY) {
    const batch = filepaths.slice(i, i + CONCURRENCY);
    const batchResults = await Promise.all(
      batch.map(async (filepath): Promise<FileScanResult | null | typeof GITLEAKS_FAILED> => {
      // gitleaks only writes its JSON findings when `--report-path` is set — it
      // never emits them on stdout (without -v it prints nothing there). Reading
      // stdout therefore always looked "clean" and silently disabled the scanner.
      // Point gitleaks at a private temp report and read the findings from it.
      const reportPath = join(tmpdir(), `tuck-gitleaks-${randomBytes(12).toString('hex')}.json`);
      try {
        try {
          // Security: Use execFileAsync with array arguments to prevent command injection
          // This prevents malicious filenames from executing arbitrary shell commands
          const { stderr } = await execFileAsync('gitleaks', [
            'detect',
            '--source', filepath,
            '--no-git',
            '--report-format', 'json',
            '--report-path', reportPath,
            '--exit-code', '0'
          ], { maxBuffer: 10 * 1024 * 1024 });

          // Check stderr for gitleaks errors (not just secret findings)
          if (stderr && stderr.trim()) {
            // Log stderr but continue - gitleaks may output warnings/errors to stderr
            console.warn(`[tuck] Gitleaks stderr for ${filepath}: ${stderr.trim()}`);
          }
        } catch (execError) {
          // Execution error - fall back to built-in scanner for this file
          const errorMsg = execError instanceof Error ? execError.message : String(execError);
          console.warn(`[tuck] Warning: Gitleaks scan failed for ${filepath}, falling back to built-in scanner: ${errorMsg}`);
          return GITLEAKS_FAILED;
        }

        // Read the report gitleaks wrote. A missing/unreadable report means it
        // produced no output we can trust, so fall back to the built-in scanner
        // rather than assuming the file is clean (fail closed for a security control).
        let reportRaw: string;
        try {
          reportRaw = await readFile(reportPath, 'utf-8');
        } catch {
          console.warn(`[tuck] Warning: Gitleaks wrote no readable report for ${filepath}, falling back to built-in scanner`);
          return GITLEAKS_FAILED;
        }

        // An empty report file means gitleaks ran and found nothing.
        if (!reportRaw.trim()) return null;

        // Security: Use Zod to validate external JSON input
        // This prevents prototype pollution and ensures type safety
        let gitleaksResults: GitleaksResult[];
        try {
          const rawData = JSON.parse(reportRaw);
          const validated = gitleaksOutputSchema.safeParse(rawData);

          if (!validated.success) {
            // Validation failed - fall back to built-in scanner for this file
            console.warn(`[tuck] Warning: Invalid gitleaks output format for ${filepath}, falling back to built-in scanner`);
            return GITLEAKS_FAILED;
          }

          gitleaksResults = validated.data;
        } catch (parseError) {
          // Parse error - fall back to built-in scanner for this file
          console.warn(`[tuck] Warning: Failed to parse gitleaks JSON for ${filepath}, falling back to built-in scanner`);
          return GITLEAKS_FAILED;
        }

        if (gitleaksResults.length === 0) return null;

        const matches: SecretMatch[] = gitleaksResults.map(gitleaksResultToMatch);

        // Collapse home directory in path for display using utility function
        const collapsedPath = collapsePath(filepath);

        return {
          path: filepath,
          collapsedPath,
          hasSecrets: true,
          matches,
          criticalCount: matches.filter(m => m.severity === 'critical').length,
          highCount: matches.filter(m => m.severity === 'high').length,
          mediumCount: matches.filter(m => m.severity === 'medium').length,
          lowCount: matches.filter(m => m.severity === 'low').length,
          skipped: false,
        };
      } finally {
        // Never leave the (secret-bearing) temp report on disk.
        await unlink(reportPath).catch(() => undefined);
      }
    })
    );

    // Process batch results
    for (let j = 0; j < batchResults.length; j++) {
      const result = batchResults[j];
      if (result === GITLEAKS_FAILED) {
        // Track this file for fallback scanning
        failedFiles.push(batch[j]);
      } else if (result) {
        results.push(result);
        filesWithSecrets++;
        totalSecrets += result.matches.length;
        for (const match of result.matches) {
          bySeverity[match.severity]++;
        }
      }
    }
  }

  // Fall back to built-in scanner for files where gitleaks failed
  if (failedFiles.length > 0) {
    console.warn(`[tuck] Running built-in scanner as fallback for ${failedFiles.length} file(s)`);
    const fallbackResults = await builtinScanFiles(failedFiles);

    // Merge fallback results
    for (const result of fallbackResults.results) {
      results.push(result);
      if (result.hasSecrets) {
        filesWithSecrets++;
        totalSecrets += result.matches.length;
        for (const match of result.matches) {
          bySeverity[match.severity]++;
        }
      }
    }
  }

  return {
    results,
    totalFiles: filepaths.length,
    scannedFiles: filepaths.length,
    skippedFiles: 0,
    filesWithSecrets,
    totalSecrets,
    bySeverity,
  };
};

// ============================================================================
// Scanner Factory
// ============================================================================

/**
 * Scan files using the specified scanner (or fallback to builtin)
 */
export const scanWithScanner = async (
  filepaths: string[],
  scanner: ExternalScanner = 'builtin'
): Promise<ScanSummary> => {
  if (scanner === 'gitleaks') {
    const isInstalled = await isGitleaksInstalled();
    if (isInstalled) {
      return scanWithGitleaks(filepaths);
    }
    // Fall back to builtin if gitleaks not available
    console.warn('gitleaks not found, falling back to built-in scanner');
  }

  if (scanner === 'trufflehog') {
    // Trufflehog integration could be added here
    // For now, fall back to builtin
    console.warn('trufflehog integration not yet implemented, using built-in scanner');
  }

  // Use built-in scanner
  return builtinScanFiles(filepaths);
};
